import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus, Calendar, Clock, AlertTriangle,
  CheckCircle, Info, Loader2, Save, Edit3,
  RotateCcw, X, Users, User, Layers, ShieldAlert, RefreshCw,
  CalendarOff, CalendarCheck, Trash2, Ban
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { buildApiUrl } from '../../services/apiHelpers';

/* ─────────────────────────────── helpers (unchanged from source) ─────────────────────────────── */
const getTodayISO = () => new Date().toISOString().split("T")[0];
const getTomorrowISO = () => {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};
const getPlusDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split("T")[0]; };
const durationOptions = [5, 10, 15, 20, 30];
const pad = (v) => String(v).padStart(2, "0");
const getRoundedCurrentTimeISO = () => {
  const now = new Date();
  const mins = Math.ceil(now.getMinutes() / 5) * 5;
  if (mins === 60) { now.setHours(now.getHours() + 1); now.setMinutes(0); }
  else now.setMinutes(mins);
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
};
const toTimeString = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const addMinutes = (date, m) => { const n = new Date(date); n.setMinutes(n.getMinutes() + m); return n; };
const normalizeApiDate = (v) => {
  if (!v) return v;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = v.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
};
const makeEmptyRow = (bookingDate) => {
  if (bookingDate === getTodayISO()) {
    const startTime = getRoundedCurrentTimeISO();
    const [h, m] = startTime.split(":").map(Number);
    const s = new Date(); s.setHours(h, m, 0, 0);
    const e = addMinutes(s, 240);
    if (e.getDate() !== s.getDate()) e.setHours(23, 59, 0, 0);
    return { startTime, endTime: toTimeString(e), slotDuration: 15 };
  }
  return { startTime: "09:00", endTime: "13:00", slotDuration: 15 };
};
const isSlotTimePast = (bd, time) => {
  if (bd !== getTodayISO()) return false;
  const now = new Date();
  const [h, m] = time.split(":").map(Number);
  const c = new Date(); c.setHours(h, m, 0, 0);
  return c < now;
};
const normalizeRowsForBookingDate = (rows, bd) => {
  if (bd !== getTodayISO()) return rows;
  const now = new Date();
  const rn = new Date(now);
  const cm = Math.ceil(now.getMinutes() / 5) * 5;
  if (cm === 60) { rn.setHours(now.getHours() + 1); rn.setMinutes(0); }
  else rn.setMinutes(cm);
  rn.setSeconds(0); rn.setMilliseconds(0);
  const normalized = rows.map((row) => {
    const s = new Date(`1970-01-01T${row.startTime}:00`);
    const e = new Date(`1970-01-01T${row.endTime}:00`);
    if (e <= now) return null;
    if (s < now) {
      if (rn >= e) return null;
      const dur = Number(row.slotDuration) || 15;
      let ae = new Date(e);
      const diff = (ae - rn) / 60000;
      const rem = diff % dur;
      if (rem !== 0) ae = addMinutes(ae, -rem);
      if (ae <= rn) return null;
      return { ...row, startTime: toTimeString(rn), endTime: toTimeString(ae) };
    }
    return row;
  }).filter(Boolean);
  return normalized.length ? normalized : [makeEmptyRow(bd)];
};
const validateRows = (rows, bd) => {
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r.startTime || !r.endTime || !r.slotDuration)
      return "Every session row needs a start time, end time and slot duration.";
    if (r.startTime === r.endTime) return `Session ${i + 1}: start time must be before end time.`;
    if (isSlotTimePast(bd, r.startTime)) return `Session ${i + 1}: start time is already past for today.`;
    const dur = Number(r.slotDuration);
    if (!durationOptions.includes(dur)) return `Session ${i + 1}: slot duration must be one of ${durationOptions.join(", ")}.`;
    let s = new Date(`1970-01-01T${r.startTime}:00`);
    let e = new Date(`1970-01-01T${r.endTime}:00`);
    if (e <= s) e = new Date(e.getTime() + 24 * 60 * 60000);
    const mins = (e - s) / 60000;
    if (mins % dur !== 0) return `Session ${i + 1}: slot duration must divide the interval evenly.`;
  }
  const sorted = rows.map((r, i) => {
    let s = new Date(`1970-01-01T${r.startTime}:00`);
    let e = new Date(`1970-01-01T${r.endTime}:00`);
    if (e <= s) e = new Date(e.getTime() + 24 * 60 * 60000);
    return { i, s, e };
  }).sort((a, b) => a.s - b.s);
  for (let i = 1; i < sorted.length; i++)
    if (sorted[i].s < sorted[i - 1].e) return `Session ${sorted[i].i + 1} overlaps another session.`;
  return null;
};

const findOverlapWithExistingSchedule = (rows, savedSchedule) => {
  if (!Array.isArray(savedSchedule) || savedSchedule.length === 0) return null;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    let rs = new Date(`1970-01-01T${r.startTime}:00`);
    let re = new Date(`1970-01-01T${r.endTime}:00`);
    if (re <= rs) re = new Date(re.getTime() + 24 * 60 * 60000);
    for (let j = 0; j < savedSchedule.length; j++) {
      const s2 = savedSchedule[j];
      let es = new Date(`1970-01-01T${s2.startTime}:00`);
      let ee = new Date(`1970-01-01T${s2.endTime}:00`);
      if (ee <= es) ee = new Date(ee.getTime() + 24 * 60 * 60000);
      if (rs < ee && es < re) {
        return `Session ${savedSchedule.length + i + 1} overlaps the saved session ${j + 1} (${s2.startTime}-${s2.endTime}). Please adjust the new session or edit the saved schedule instead.`;
      }
    }
  }
  return null;
};

const toPayloadRows = (rows) => rows.map((r) => {
  const payload = { startTime: r.startTime, endTime: r.endTime, slotDuration: Number(r.slotDuration) };
  if (r.scheduleId && r.scheduleId.toString().trim() !== "") payload.scheduleId = r.scheduleId;
  return payload;
});
const isSlotInsideRows = (slotTime, rows) => {
  if (!slotTime || !rows?.length) return false;
  try {
    const t = new Date(`1970-01-01T${slotTime.substring(0, 5)}:00`);
    for (const r of rows) {
      const s = new Date(`1970-01-01T${(r.startTime || "").substring(0, 5)}:00`);
      const e = new Date(`1970-01-01T${(r.endTime || "").substring(0, 5)}:00`);
      if (t >= s && t < e) return true;
    }
  } catch { return false; }
  return false;
};
const getTotalSlotsForRows = (rows) => rows.reduce((sum, r) => {
  let s = new Date(`1970-01-01T${r.startTime}:00`);
  let e = new Date(`1970-01-01T${r.endTime}:00`);
  if (e <= s) e = new Date(e.getTime() + 24 * 60 * 60000);
  const d = Number(r.slotDuration) || 15;
  return sum + Math.max(0, Math.floor((e - s) / 60000 / d));
}, 0);
const getTotalBookings = (sessions) => sessions.reduce((s, x) => s + Number(x.totalBookings || 0), 0);

/* ── Day timeline helpers (6am–10pm range) ── */
const TL_START = 6, TL_END = 22;
const timePct = (t) => {
  if (!t) return 0;
  const [h, m] = String(t).slice(0, 5).split(":").map(Number);
  const val = h + (m || 0) / 60;
  return Math.min(100, Math.max(0, ((val - TL_START) / (TL_END - TL_START)) * 100));
};
const timelineStyle = (start, end) => {
  const left = timePct(start);
  const width = Math.max(2, timePct(end) - left);
  return { left: `${left}%`, width: `${width}%` };
};
const TL_HOURS = (() => { const h = []; for (let i = TL_START; i <= TL_END; i += 2) h.push(i); return h; })();
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "am" : "pm"}`;

/* ─────────────── Slot count calculator ─────────────── */
const calcSlots = (startTime, endTime, slotDuration) => {
  if (!startTime || !endTime) return 0;
  const s = new Date(`1970-01-01T${startTime}:00`);
  const e = new Date(`1970-01-01T${endTime}:00`);
  const dur = Number(slotDuration) || 15;
  return Math.max(0, Math.floor((e - s) / 60000 / dur));
};

/* ═══════════════════════════ PREMIUM BLUE THEME ═══════════════════════════ */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500&family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@500&display=swap');

  :root{
    --opd-navy-950:#050b1e; --opd-navy-900:#0a1638; --opd-navy-800:#0f1f4d;
    --opd-blue-700:#1749d6; --opd-blue-600:#2f5fe8; --opd-blue-500:#5478ee;
    --opd-blue-100:#eef2fd; --opd-blue-150:#e3eafc; --opd-blue-200:#c8d6f8;
    --opd-ink:#0d1530; --opd-ink-2:#3a4468; --opd-ink-3:#6b7494; --opd-ink-4:#9aa3c0;
    --opd-surface:#ffffff; --opd-surface-2:#f6f8fd; --opd-surface-3:#eef1fa;
    --opd-line:#e2e7f5; --opd-line-2:#cdd7f0;
    --opd-green:#187a4a; --opd-green-bg:#eaf7ef; --opd-green-line:#bfe6cf;
    --opd-amber:#a8620a; --opd-amber-bg:#fdf3e4; --opd-amber-line:#f0d6a3;
    --opd-red:#b3372c; --opd-red-bg:#fbecea; --opd-red-line:#f0c6c0;
    --opd-r-sm:10px; --opd-r-md:14px; --opd-r-lg:20px; --opd-r-xl:26px;
    --opd-sh-sm:0 1px 2px rgba(9,16,40,.06), 0 1px 1px rgba(9,16,40,.04);
    --opd-sh-md:0 10px 30px -8px rgba(9,16,40,.16);
    --opd-sh-lg:0 30px 70px -20px rgba(6,12,34,.35);
    --opd-font-disp:'Fraunces', Georgia, serif;
    --opd-font-body:'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    --opd-font-mono:'IBM Plex Mono', ui-monospace, monospace;
  }
  .opd-shell *{box-sizing:border-box}
  .opd-shell{ font-family:var(--opd-font-body); color:var(--opd-ink); background:var(--opd-surface-2); min-height:100vh; }
  .opd-page{ max-width:1300px; margin:0 auto; padding:22px 24px 0; }
  .opd-shell button{font-family:inherit}

  /* Topbar */
  .opd-topbar{display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:20px;}
  .opd-topbar-title{display:flex; align-items:center; gap:12px;}
  .opd-avatar{width:44px; height:44px; border-radius:13px; background:linear-gradient(150deg,var(--opd-blue-600),var(--opd-navy-800)); display:grid; place-items:center; color:#fff; flex:none;}
  .opd-topbar-title h1{font-family:var(--opd-font-disp); font-size:19px; font-weight:600; margin:0; letter-spacing:-.01em;}
  .opd-topbar-doctor-name{color:var(--opd-blue-700);}
  .opd-topbar-title p{margin:2px 0 0; font-size:12.5px; color:var(--opd-ink-3);}
  .opd-doc-status{display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; padding:4px 10px; border-radius:999px; border:1px solid;}
  .opd-doc-status.ok{color:var(--opd-green); background:var(--opd-green-bg); border-color:var(--opd-green-line);}
  .opd-doc-status.off{color:var(--opd-amber); background:var(--opd-amber-bg); border-color:var(--opd-amber-line);}
  .opd-doc-status i{width:6px; height:6px; border-radius:50%; background:currentColor; display:inline-block;}

  /* Hero */
  .opd-hero{ position:relative; overflow:hidden; border-radius:var(--opd-r-xl); padding:30px 32px; margin-bottom:22px; color:#eef1fc;
    background: radial-gradient(120% 150% at 100% -20%, rgba(84,120,238,.25), transparent 55%), linear-gradient(160deg,#040a1c 0%, #0a1638 45%, #12318f 100%);
    box-shadow:0 26px 60px -28px rgba(4,10,28,.55); }
  .opd-hero::after{content:"";position:absolute;inset:0;border-radius:var(--opd-r-xl);box-shadow:inset 0 1px 0 rgba(255,255,255,.14);pointer-events:none}
  .opd-hero-eyebrow{font-family:var(--opd-font-mono); font-size:11px; letter-spacing:.08em; color:#93a6e6; margin-bottom:9px;}
  .opd-hero h2{font-family:var(--opd-font-disp); font-weight:500; font-size:clamp(21px,2.4vw,28px); line-height:1.25; margin:0; max-width:34ch; letter-spacing:-.01em; position:relative; color:#f4f7ff;}
  .opd-hero p{margin:9px 0 0; color:#b6c1ea; font-size:13.5px; line-height:1.6; max-width:58ch; position:relative;}
  .opd-hero-grid{display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; margin-top:22px; position:relative;}
  .opd-hstat{border-radius:15px; padding:14px 16px; background:rgba(255,255,255,.055); border:1px solid rgba(255,255,255,.12);}
  .opd-hstat b{display:block; font-family:var(--opd-font-disp); font-size:22px; font-weight:600; font-variant-numeric:tabular-nums;}
  .opd-hstat span{display:block; font-size:11px; color:#9fb0e3; margin-top:3px;}

  /* Stepper */
  .opd-stepper{display:flex; align-items:center; justify-content:center; background:var(--opd-surface); border:1px solid var(--opd-line); border-radius:var(--opd-r-lg); padding:16px 20px; margin-bottom:22px; overflow-x:auto; box-shadow:var(--opd-sh-sm);}
  .opd-step2{display:flex; align-items:center; gap:10px; flex:none;}
  .opd-step2-dot{width:28px; height:28px; border-radius:50%; display:grid; place-items:center; font-family:var(--opd-font-mono); font-size:12px; font-weight:700; flex:none;}
  .opd-step2.done .opd-step2-dot{background:var(--opd-blue-700); color:#fff;}
  .opd-step2.active .opd-step2-dot{background:var(--opd-blue-100); color:var(--opd-blue-700); border:2px solid var(--opd-blue-700);}
  .opd-step2.todo .opd-step2-dot{background:var(--opd-surface-3); color:var(--opd-ink-4);}
  .opd-step2-label{font-size:13px; font-weight:600; white-space:nowrap;}
  .opd-step2.done .opd-step2-label{color:var(--opd-ink-3);}
  .opd-step2.active .opd-step2-label{color:var(--opd-ink);}
  .opd-step2.todo .opd-step2-label{color:var(--opd-ink-4);}
  .opd-step2-rule{width:44px; height:1.5px; background:var(--opd-line-2); margin:0 14px; flex:none;}

  /* Layout */
  .opd-layout{display:grid; grid-template-columns:1fr 360px; gap:24px; align-items:start;}
  .opd-col-main, .opd-col-side{display:flex; flex-direction:column; gap:18px;}
  .opd-col-side{position:sticky; top:20px;}
  .opd-card{background:var(--opd-surface); border:1px solid var(--opd-line); border-radius:var(--opd-r-xl); box-shadow:var(--opd-sh-sm); padding:24px;}
  .opd-card-head{display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:16px; flex-wrap:wrap;}
  .opd-card-head h3{font-family:var(--opd-font-disp); font-size:16.5px; font-weight:600; margin:0; display:flex; align-items:center; gap:8px;}
  .opd-card-sub{font-size:12.5px; color:var(--opd-ink-3); margin-top:2px;}
  .opd-head-icon{width:30px; height:30px; border-radius:9px; background:var(--opd-blue-100); color:var(--opd-blue-700); display:inline-flex; align-items:center; justify-content:center; flex:none;}

  /* Date control */
  .opd-date-row{display:flex; gap:10px; flex-wrap:wrap; align-items:center;}
  .opd-chip2{border:1.5px solid var(--opd-line-2); background:var(--opd-surface); color:var(--opd-ink-3); padding:8px 15px; border-radius:999px; font-size:12.5px; font-weight:700; cursor:pointer; transition:.15s;}
  .opd-chip2.active{background:var(--opd-blue-700); border-color:var(--opd-blue-700); color:#fff; box-shadow:0 6px 14px rgba(23,73,214,.25);}
  .opd-chip2:not(.active):hover{border-color:var(--opd-blue-500); color:var(--opd-blue-700);}
  .opd-date-picker2{margin-left:auto; display:flex; align-items:center; gap:8px; border:1.5px solid var(--opd-line-2); border-radius:var(--opd-r-sm); padding:0 12px; height:42px; background:var(--opd-surface-2);}
  .opd-date-picker2 input{border:none; background:transparent; font-size:13.5px; font-weight:600; color:var(--opd-ink); width:140px; font-family:inherit;}
  .opd-date-picker2 input:focus{outline:none;}
  .opd-date-picker2 svg{color:var(--opd-blue-600); flex:none;}

  /* Timeline */
  .opd-timeline2{position:relative; height:58px; border-radius:var(--opd-r-md); background:var(--opd-surface-3); border:1px solid var(--opd-line); overflow:hidden; margin-top:4px;}
  .opd-tl2-tick{position:absolute; top:0; bottom:0; width:1px; background:var(--opd-line-2);}
  .opd-tl2-label{position:absolute; bottom:4px; transform:translateX(-50%); font-family:var(--opd-font-mono); font-size:9px; color:var(--opd-ink-4);}
  .opd-tl2-block{position:absolute; top:9px; height:24px; border-radius:7px; display:flex; align-items:center; padding:0 9px; font-size:10.5px; font-weight:700; white-space:nowrap; overflow:hidden; color:#fff;}
  .opd-tl2-block.saved{background:linear-gradient(135deg,var(--opd-blue-700),var(--opd-navy-800));}
  .opd-tl2-block.new{background:linear-gradient(135deg,#4f7bf0,var(--opd-blue-600)); box-shadow:0 0 0 2px #fff inset;}
  .opd-tl2-legend{display:flex; gap:16px; margin-top:10px; font-size:11.5px; color:var(--opd-ink-3);}
  .opd-tl2-legend span{display:inline-flex; align-items:center; gap:6px;}
  .opd-tl2-legend i{width:9px; height:9px; border-radius:3px; display:inline-block;}

  /* Saved rows */
  .opd-saved-row2{display:grid; grid-template-columns:auto 1fr auto; gap:14px; padding:13px 14px; border-radius:var(--opd-r-md); background:var(--opd-blue-100); border:1px solid var(--opd-blue-200); align-items:center; margin-bottom:9px;}
  .opd-saved-row2:last-child{margin-bottom:0;}
  .opd-row-icon2{width:32px; height:32px; border-radius:9px; background:#fff; color:var(--opd-blue-700); display:grid; place-items:center; flex:none;}
  .opd-saved-row2 .opd-rtitle{font-weight:700; font-size:13.5px;}
  .opd-saved-row2 .opd-rmeta{color:var(--opd-ink-3); font-size:11.5px; margin-top:2px;}
  .opd-rcount2{font-family:var(--opd-font-disp); font-weight:600; color:var(--opd-blue-700); font-size:17px;}
  .opd-badge2{background:#dde7fd; color:var(--opd-blue-700); padding:3px 9px; border-radius:999px; font-family:var(--opd-font-mono); font-size:10px; font-weight:700;}
  .opd-btn-ghost2{height:34px; padding:0 13px; border-radius:999px; border:1.5px solid var(--opd-blue-200); background:var(--opd-blue-100); color:var(--opd-blue-700); font-size:12.5px; font-weight:700; display:inline-flex; align-items:center; gap:6px; cursor:pointer; transition:.15s;}
  .opd-btn-ghost2:hover{background:var(--opd-blue-200);}

  /* Session cards */
  .opd-sessions2{display:flex; flex-direction:column; gap:14px;}
  .opd-session2{position:relative; border:1.5px solid var(--opd-line); border-radius:var(--opd-r-lg); padding:18px 20px 18px 22px; background:var(--opd-surface); transition:.18s; overflow:hidden;}
  .opd-session2::before{content:"";position:absolute; left:0; top:0; bottom:0; width:4px; background:linear-gradient(180deg,var(--opd-blue-500),var(--opd-blue-700));}
  .opd-session2:hover{box-shadow:var(--opd-sh-md); border-color:var(--opd-blue-200);}
  .opd-session2-head{display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; gap:10px; flex-wrap:wrap;}
  .opd-session2-title{display:flex; align-items:center; gap:9px; font-weight:700; font-size:14px;}
  .opd-session2-num{width:24px; height:24px; border-radius:50%; background:var(--opd-blue-700); color:#fff; font-family:var(--opd-font-mono); font-size:11px; display:grid; place-items:center; flex:none;}
  .opd-icon-btn2{border:1.5px solid var(--opd-line); background:var(--opd-surface-2); color:var(--opd-ink-3); border-radius:999px; padding:6px 13px; font-size:12px; font-weight:600; display:inline-flex; align-items:center; gap:6px; cursor:pointer; transition:.15s;}
  .opd-icon-btn2:hover:not(:disabled){border-color:var(--opd-red-line); color:var(--opd-red); background:var(--opd-red-bg);}
  .opd-icon-btn2:disabled{opacity:.45; cursor:not-allowed;}
  .opd-grid3{display:grid; grid-template-columns:repeat(3,1fr); gap:14px;}
  .opd-field2{display:flex; flex-direction:column; gap:6px;}
  .opd-field2 label{font-size:11.5px; font-weight:700; color:var(--opd-ink-3); display:flex; align-items:center; gap:5px;}
  .opd-field2 label svg{color:var(--opd-blue-600);}
  .opd-input2{height:42px; padding:0 12px; border-radius:var(--opd-r-sm); border:1.5px solid var(--opd-line-2); background:var(--opd-surface); color:var(--opd-ink); font-size:13.5px; font-weight:500; width:100%; transition:.15s;}
  .opd-input2:focus{outline:none; border-color:var(--opd-blue-500); box-shadow:0 0 0 4px var(--opd-blue-150);}
  .opd-input2:disabled{background:var(--opd-surface-3); color:var(--opd-ink-4); cursor:not-allowed;}
  .opd-slot-pill2{display:inline-flex; align-items:center; gap:6px; margin-top:12px; padding:6px 12px; border-radius:999px; background:var(--opd-blue-100); color:var(--opd-blue-700); font-family:var(--opd-font-mono); font-size:11.5px; font-weight:700; border:1px solid var(--opd-blue-200);}

  .opd-add-session2{display:flex; align-items:center; justify-content:center; gap:8px; height:48px; border-radius:var(--opd-r-lg); border:1.5px dashed var(--opd-line-2); background:var(--opd-surface-2); color:var(--opd-blue-700); font-weight:700; font-size:13.5px; cursor:pointer; transition:.15s; width:100%; margin-top:14px;}
  .opd-add-session2:hover:not(:disabled){border-color:var(--opd-blue-500); background:var(--opd-blue-100);}
  .opd-add-session2:disabled{opacity:.5; cursor:not-allowed;}

  /* Sidebar */
  .opd-leave-card2{border-radius:var(--opd-r-lg); border:1.5px solid var(--opd-line); padding:18px; background:var(--opd-surface-2);}
  .opd-leave-card2.on{background:var(--opd-amber-bg); border-color:var(--opd-amber-line);}
  .opd-leave-status-row2{display:flex; align-items:center; gap:10px;}
  .opd-leave-icon2{width:36px; height:36px; border-radius:11px; background:#fff; display:grid; place-items:center; color:var(--opd-ink-3); flex:none; border:1px solid var(--opd-line);}
  .opd-leave-card2.on .opd-leave-icon2{color:var(--opd-amber); border-color:var(--opd-amber-line);}
  .opd-leave-title2{font-weight:700; font-size:13.5px;}
  .opd-leave-sub2{font-size:12px; color:var(--opd-ink-3); margin-top:1px;}
  .opd-btn-block2{width:100%; height:42px; border-radius:var(--opd-r-sm); border:1.5px solid var(--opd-line-2); background:var(--opd-surface); color:var(--opd-ink); font-size:13px; font-weight:700; margin-top:14px; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px; transition:.15s;}
  .opd-btn-block2:hover:not(:disabled){background:var(--opd-surface-3);}
  .opd-btn-block2:disabled{opacity:.6; cursor:not-allowed;}

  .opd-summary-list2{display:flex; flex-direction:column; gap:10px;}
  .opd-summary-item2{display:flex; justify-content:space-between; align-items:center; font-size:12.5px; padding:9px 0; border-bottom:1px solid var(--opd-line);}
  .opd-summary-item2:last-child{border-bottom:none;}
  .opd-summary-item2 b{font-family:var(--opd-font-disp); font-size:16px; font-weight:600; color:var(--opd-blue-700);}
  .opd-tips-list2{margin:0; padding-left:18px; font-size:12.5px; color:var(--opd-ink-3); line-height:1.85;}
  .opd-tips-list2 li{margin-bottom:2px;}

  /* Alerts */
  .opd-alert2{padding:13px 15px; border-radius:var(--opd-r-md); display:flex; align-items:flex-start; gap:10px; font-size:13px; font-weight:500; margin-top:16px;}
  .opd-alert2-error{background:var(--opd-red-bg); color:#8a2c22; border:1.5px solid var(--opd-red-line);}
  .opd-alert2-warn{background:var(--opd-amber-bg); color:#7a4b08; border:1.5px solid var(--opd-amber-line);}
  .opd-alert2-info{background:var(--opd-blue-100); color:var(--opd-navy-800); border:1.5px solid var(--opd-blue-200);}
  .opd-alert2-success{background:var(--opd-green-bg); color:#1e6640; border:1.5px solid var(--opd-green-line);}
  .opd-alert2 svg{flex:none; margin-top:1px;}

  /* Undo bar */
  .opd-undo2{margin-top:16px; display:flex; align-items:center; gap:8px; padding:10px 14px; border-radius:var(--opd-r-md); background:var(--opd-blue-100); border:1.5px solid var(--opd-blue-200); font-size:13px; color:var(--opd-navy-800); font-weight:600;}
  .opd-undo2-btn{border:none; background:transparent; color:var(--opd-blue-700); font-weight:800; cursor:pointer; font-size:13px; text-decoration:underline; padding:0;}

  /* Footer */
  .opd-footer-space{height:96px;}
  .opd-footer2{ position:fixed; left:0; right:0; bottom:0; z-index:40; background:rgba(255,255,255,.88); backdrop-filter:blur(14px);
    border-top:1px solid var(--opd-line); box-shadow:0 -12px 30px rgba(9,16,40,.06);
    padding:14px max(24px, calc((100vw - 1300px)/2)) calc(14px + env(safe-area-inset-bottom,0px)); }
  .opd-footer2-inner{max-width:1300px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap;}
  .opd-footer2-status{display:flex; align-items:center; gap:9px; font-size:12.5px; color:var(--opd-ink-3); font-weight:600;}
  .opd-dot-pulse2{width:8px; height:8px; border-radius:50%; background:var(--opd-amber); position:relative; flex:none;}
  .opd-dot-pulse2::after{content:"";position:absolute; inset:-4px; border-radius:50%; border:2px solid var(--opd-amber); opacity:.5; animation:opdPulse 1.6s ease-out infinite;}
  @keyframes opdPulse{0%{transform:scale(.6); opacity:.6} 100%{transform:scale(1.8); opacity:0}}
  .opd-footer2-actions{display:flex; gap:10px; align-items:center;}

  /* Buttons */
  .opd-btn2{height:46px; padding:0 20px; border-radius:999px; font-size:13.5px; font-weight:700; cursor:pointer; border:1.5px solid transparent; display:inline-flex; align-items:center; gap:8px; transition:.18s; white-space:nowrap;}
  .opd-btn2:disabled{opacity:.5; cursor:not-allowed;}
  .opd-btn2-primary{background:linear-gradient(135deg,var(--opd-blue-600),var(--opd-blue-700)); color:#fff; box-shadow:0 10px 24px rgba(23,73,214,.32);}
  .opd-btn2-primary:hover:not(:disabled){transform:translateY(-1px); box-shadow:0 14px 30px rgba(23,73,214,.4);}
  .opd-btn2-secondary{border-color:var(--opd-line-2); background:var(--opd-surface); color:var(--opd-ink);}
  .opd-btn2-secondary:hover:not(:disabled){background:var(--opd-surface-3);}
  .opd-btn2-danger{background:var(--opd-red); color:#fff;}
  .opd-btn2-danger:hover:not(:disabled){background:#96291f;}
  .opd-btn2-outline{border-color:var(--opd-blue-500); background:var(--opd-blue-100); color:var(--opd-blue-700);}
  .opd-btn2-outline:hover:not(:disabled){background:var(--opd-blue-200);}

  .opd-spin2{animation:opdSpin .8s linear infinite;}
  @keyframes opdSpin{to{transform:rotate(360deg)}}
  .opd-skeleton2{padding:22px; border-radius:var(--opd-r-lg); background:linear-gradient(90deg,var(--opd-surface-3) 25%, var(--opd-surface-2) 37%, var(--opd-surface-3) 63%); background-size:400% 100%; animation:opdShimmer 1.4s ease infinite; display:flex; align-items:center; gap:10px; color:var(--opd-ink-3); font-size:14px; font-weight:500;}
  @keyframes opdShimmer{0%{background-position:100% 0}100%{background-position:0 0}}

  /* Toasts */
  .opd-toast-stack{position:fixed; top:18px; right:18px; z-index:400; display:flex; flex-direction:column; gap:10px; max-width:360px;}
  .opd-toast{display:flex; align-items:flex-start; gap:10px; background:var(--opd-navy-900); color:#eef1fc; padding:13px 15px; border-radius:var(--opd-r-md); box-shadow:var(--opd-sh-lg); font-size:13px; font-weight:500; animation:opdToastIn .25s cubic-bezier(.2,.9,.3,1);}
  .opd-toast.success{background:linear-gradient(135deg,#0d3d24,#155c37);}
  .opd-toast.error{background:linear-gradient(135deg,#4a1a15,#6b241c);}
  .opd-toast svg{flex:none; margin-top:1px;}
  @keyframes opdToastIn{from{opacity:0; transform:translateX(20px)} to{opacity:1; transform:translateX(0)}}

  /* Modals */
  .opd-overlay2{position:fixed; inset:0; background:radial-gradient(120% 140% at 50% 0%, rgba(20,35,90,.4), rgba(4,8,22,.68)); backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; z-index:350; padding:20px; animation:opdFadeIn .18s ease;}
  @keyframes opdFadeIn{from{opacity:0}to{opacity:1}}
  .opd-modal2{background:var(--opd-surface); border-radius:var(--opd-r-xl); width:min(100%,520px); box-shadow:var(--opd-sh-lg); animation:opdScaleIn .22s cubic-bezier(.2,.8,.2,1); max-height:88vh; display:flex; flex-direction:column; overflow:hidden;}
  .opd-modal2-wide{width:min(100%,700px);}
  @keyframes opdScaleIn{from{opacity:0; transform:scale(.95) translateY(10px)} to{opacity:1; transform:scale(1) translateY(0)}}
  .opd-modal2-head{padding:24px 26px 6px; display:flex; gap:14px; align-items:flex-start;}
  .opd-modal2-icon{width:42px; height:42px; border-radius:13px; background:var(--opd-blue-100); display:grid; place-items:center; color:var(--opd-blue-700); flex:none;}
  .opd-modal2-icon.warn{background:var(--opd-amber-bg); color:var(--opd-amber);}
  .opd-modal2-title{font-family:var(--opd-font-disp); font-size:18.5px; font-weight:600; margin:0;}
  .opd-modal2-desc{color:var(--opd-ink-3); font-size:13px; line-height:1.55; margin:5px 0 0;}
  .opd-modal2-body{padding:16px 26px; overflow-y:auto; flex:1;}
  .opd-modal2-foot{padding:16px 26px 22px; border-top:1px solid var(--opd-line); display:flex; justify-content:flex-end; gap:10px; flex-wrap:wrap;}
  .opd-modal2 textarea.opd-input2{height:90px; padding:10px 12px; resize:vertical; font-family:inherit;}
  .opd-confirm-box2{background:var(--opd-surface-2); border:1.5px solid var(--opd-line); border-radius:var(--opd-r-md); padding:14px 16px; margin-top:6px;}
  .opd-confirm-row2{display:flex; justify-content:space-between; font-size:12.5px; padding:8px 0; border-bottom:1px solid var(--opd-line); gap:10px; flex-wrap:wrap;}
  .opd-confirm-row2:last-child{border-bottom:none;}
  .opd-confirm-total2{display:flex; justify-content:space-between; padding-top:10px; margin-top:2px; font-family:var(--opd-font-disp); font-weight:600; font-size:15.5px; color:var(--opd-blue-700);}
  .opd-affected-scroll2{max-height:320px; overflow-y:auto;}

  /* Responsive */
  @media (max-width:980px){
    .opd-layout{grid-template-columns:1fr;}
    .opd-col-side{order:-1; position:static;}
    .opd-hero-grid{grid-template-columns:repeat(2,1fr);}
  }
  @media (max-width:640px){
    .opd-page{padding:16px 14px 0;}
    .opd-hero{padding:22px 18px;}
    .opd-card{padding:18px;}
    .opd-grid3{grid-template-columns:1fr;}
    .opd-date-picker2{margin-left:0; width:100%;}
    .opd-date-picker2 input{width:100%;}
    .opd-step2-label{display:none;}
    .opd-step2-rule{width:20px; margin:0 8px;}
    .opd-footer2{padding:12px 14px calc(12px + env(safe-area-inset-bottom,0px));}
    .opd-footer2-inner{flex-direction:column-reverse; align-items:stretch;}
    .opd-footer2-actions{width:100%;}
    .opd-footer2-actions .opd-btn2{flex:1; justify-content:center;}
    .opd-modal2-foot{flex-direction:column-reverse;}
    .opd-modal2-foot button{width:100%; justify-content:center;}
    .opd-session2-head, .opd-saved-row2, .opd-affected-row2{flex-wrap:wrap;}
  }
  @media (prefers-reduced-motion: reduce){
    .opd-toast, .opd-overlay2, .opd-modal2, .opd-hstat{animation:none !important;}
  }
`;

/* ═══════════════════════════════ TOAST STACK ══════════════════════════════ */
function ToastStack({ toasts }) {
  return (
    <div className="opd-toast-stack">
      {toasts.map((t) => (
        <div key={t.id} className={`opd-toast ${t.type === "success" ? "success" : t.type === "error" ? "error" : ""}`}>
          {t.type === "success" ? <CheckCircle size={16} /> : t.type === "error" ? <AlertTriangle size={16} /> : <Info size={16} />}
          <span>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════ CONFIRM MODAL ══════════════════════════════ */
function ConfirmModal({ open, title, description, details, primaryLabel, onConfirm, onCancel, loading, isDanger }) {
  if (!open) return null;
  return (
    <div className="opd-overlay2">
      <div className="opd-modal2">
        <div className="opd-modal2-head">
          <span className={`opd-modal2-icon ${isDanger ? "warn" : ""}`}>
            {isDanger ? <ShieldAlert size={19} /> : <CheckCircle size={19} />}
          </span>
          <div>
            <h2 className="opd-modal2-title">{title}</h2>
            <p className="opd-modal2-desc">{description}</p>
          </div>
        </div>
        <div className="opd-modal2-body">{details}</div>
        <div className="opd-modal2-foot">
          <button type="button" className="opd-btn2 opd-btn2-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button type="button" className={`opd-btn2 ${isDanger ? "opd-btn2-danger" : "opd-btn2-primary"}`} onClick={onConfirm} disabled={loading}>
            {loading ? <><Loader2 size={15} className="opd-spin2" /> Processing…</> : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════ MAIN COMPONENT ══════════════════════════════ */
export default function DoctorOpdOpenBookings() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [doctorName, setDoctorName] = useState("");
  const [bookingDate, setBookingDate] = useState(getTomorrowISO());
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [leaveState, setLeaveState] = useState({ onLeave: false, leaveReason: "" });
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveReasonDraft, setLeaveReasonDraft] = useState("");
  const [loadingLeave, setLoadingLeave] = useState(false);
  const [existingSessions, setExistingSessions] = useState([]);
  const [existingSchedule, setExistingSchedule] = useState([]);
  const [showEditSavedModal, setShowEditSavedModal] = useState(false);
  const [editModalRows, setEditModalRows] = useState([]);
  const [savedRowsBackup, setSavedRowsBackup] = useState(null);
  const [undoAvailable, setUndoAvailable] = useState(false);
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingActionData, setPendingActionData] = useState(null);
  const [affectedAppointments, setAffectedAppointments] = useState([]);
  const [showAffectedModal, setShowAffectedModal] = useState(false);
  const [notifyPatientsMode, setNotifyPatientsMode] = useState(false);
  const [reschedulingId, setReschedulingId] = useState(null);
  const [availableSlotsForReschedule, setAvailableSlotsForReschedule] = useState([]);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenScheduleId, setReopenScheduleId] = useState(null);
  const [reopeningSession, setReopeningSession] = useState(false);
  const [shouldReopenAfterSave, setShouldReopenAfterSave] = useState(false);
  const [endedSessionStatus, setEndedSessionStatus] = useState({});
  const [toasts, setToasts] = useState([]);

  /* ── toast + notify helpers ── */
  const pushToast = (msg, type = "info") => {
    if (!msg) return;
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3400);
  };
  const notifyError = (msg) => { setError(msg); if (msg) pushToast(msg, "error"); };
  const notifySuccess = (msg) => { setMessage(msg); setError(null); if (msg) pushToast(msg, "success"); };

  /* ── data fetching ── */
  useEffect(() => {
    const loadDoctorName = async () => {
      const clinicId = auth?.clinic?.id;
      if (!doctorId || !clinicId) { setDoctorName(""); return; }
      setDoctorName("");
      try {
        const response = await fetch(buildApiUrl(`doctors/by-clinic-id/${clinicId}`), {
          headers: { "X-Clinic-Id": String(clinicId) },
          credentials: "include",
        });
        if (!response.ok) return;
        const doctors = await response.json();
        const doctor = Array.isArray(doctors)
          ? doctors.find((item) => String(item.id) === String(doctorId))
          : null;
        setDoctorName(doctor?.doctor_name || doctor?.name || "");
      } catch {
        setDoctorName("");
      }
    };
    loadDoctorName();
  }, [doctorId, auth?.clinic?.id]);

  useEffect(() => {
    const load = async () => {
      if (!doctorId || !bookingDate) return;
      setLoading(true); setError(null); setMessage(null);
      const td = normalizeApiDate(bookingDate);
      try {
        const r = await fetch(buildApiUrl(`slots/schedule?doctorId=${doctorId}&date=${td}`));
        const d = await r.json();
        if (Array.isArray(d) && d.length > 0) {
          const sm = {};
          const mapped = d.map((s) => {
            const start = s.startTime?.substring(0, 5) || "09:00";
            const end = s.endTime?.substring(0, 5) || "13:00";
            const dur = s.slotDuration || 15;
            const mins = Math.max(0, (new Date(`1970-01-01T${end}:00`) - new Date(`1970-01-01T${start}:00`)) / 60000);
            if (s.scheduleId) sm[s.scheduleId] = s.opdStatus || "NOT_STARTED";
            return { startTime: start, endTime: end, slotDuration: dur, slotsCount: Math.floor(mins / dur), scheduleId: s.scheduleId, opdStatus: s.opdStatus || "NOT_STARTED" };
          });
          setEndedSessionStatus(sm);
          setExistingSchedule(mapped);
          setRows([]);
        } else {
          setExistingSchedule([]);
          const dr = await fetch(buildApiUrl(`slots/schedule/default?doctorId=${doctorId}`));
          const dd = await dr.json();
          if (Array.isArray(dd) && dd.length > 0) {
            setRows(dd.map((s) => ({ startTime: s.startTime?.substring(0, 5) || "09:00", endTime: s.endTime?.substring(0, 5) || "13:00", slotDuration: s.slotDuration || 15 })));
          } else setRows([makeEmptyRow(td)]);
        }
      } catch { notifyError("Unable to load schedule. Please try again."); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, bookingDate]);

  useEffect(() => {
    const fetchLeave = async () => {
      if (!doctorId || !bookingDate) { setLeaveState({ onLeave: false, leaveReason: "" }); return; }
      setLoadingLeave(true);
      try {
        const nd = normalizeApiDate(bookingDate);
        const r = await fetch(buildApiUrl(`slots/leave?doctorId=${doctorId}&date=${nd}`));
        const d = await r.json();
        const ns = { onLeave: Boolean(d.onLeave), leaveReason: d.leaveReason || "" };
        setLeaveState(ns);
        if (ns.onLeave) { setRows([makeEmptyRow(bookingDate)]); setExistingSchedule([]); }
      } catch { setLeaveState({ onLeave: false, leaveReason: "" }); }
      finally { setLoadingLeave(false); }
    };
    fetchLeave();
  }, [doctorId, bookingDate]);

  useEffect(() => {
    const fetch2 = async () => {
      if (!doctorId || !bookingDate) { setExistingSessions([]); return; }
      try {
        const nd = normalizeApiDate(bookingDate);
        const cid = auth?.clinic?.id || "";
        const r = await fetch(buildApiUrl(`doctor-sessions/${doctorId}/today?sessionDate=${nd}`), { headers: { "X-Clinic-Id": cid || "" } });
        const d = await r.json();
        setExistingSessions(d?.status === "success" && Array.isArray(d.sessions) ? d.sessions : []);
      } catch { setExistingSessions([]); }
    };
    fetch2();
  }, [doctorId, bookingDate, auth?.clinic?.id]);

  useEffect(() => {
    if (!showAffectedModal) return;
    (async () => {
      try {
        const r = await fetch(buildApiUrl(`slots?doctorId=${doctorId}&date=${normalizeApiDate(bookingDate)}`));
        const d = await r.json();
        const slots = Array.isArray(d.slots) ? d.slots.map((s) => s.slotTime || s.slot) : [];
        const base = pendingActionData?.rows || rows;
        setAvailableSlotsForReschedule(slots.filter((st) => isSlotInsideRows((st || "").substring(0, 5), base)));
      } catch { setAvailableSlotsForReschedule([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAffectedModal, doctorId, bookingDate, pendingActionData?.rows, rows]);

  useEffect(() => {
    if (bookingDate !== getTodayISO()) return;
    setRows((pr) => normalizeRowsForBookingDate(pr, bookingDate));
  }, [bookingDate]);

  /* ── row helpers ── */
  const updateRow = (i, f, v) => { const n = [...rows]; n[i] = { ...n[i], [f]: v }; setRows(n); };
  const updateEditRow = (i, f, v) => { const n = [...editModalRows]; n[i] = { ...n[i], [f]: v }; setEditModalRows(n); };
  const addRow = () => { setRows([...rows, makeEmptyRow(bookingDate)]); pushToast("Session added — set its time below", "info"); };
  const removeRow = (i) => {
    const n = rows.filter((_, idx) => idx !== i);
    setRows(n.length ? n : [makeEmptyRow(bookingDate)]);
    pushToast("Session removed from draft", "info");
  };
  const removeEditRow = async (i) => {
    const targetRow = editModalRows[i];
    const nextRows = editModalRows.filter((_, idx) => idx !== i);
    const normalizedNextRows = normalizeRowsForBookingDate(nextRows.length ? nextRows : [makeEmptyRow(bookingDate)], bookingDate);
    setEditModalRows(normalizedNextRows);

    if (!targetRow?.scheduleId) return;

    try {
      const cid = auth?.clinic?.id || "";
      const r = await fetch(buildApiUrl(`appointments/doctor/bookings?scheduleId=${targetRow.scheduleId}`), {
        headers: { "X-Clinic-Id": cid || "" },
      });
      const d = await r.json();
      const affected = Array.isArray(d)
        ? d.filter((a) => {
            const status = String(a?.status || a?.bookingStatus || a?.appointmentStatus || "").toUpperCase();
            return status !== "COMPLETED" && status !== "CANCELLED" && status !== "RESCHEDULED";
          })
        : [];

      if (!affected.length) return;

      setAffectedAppointments(affected);
      setPendingAction("saveSchedule");
      setPendingActionData({ rows: normalizedNextRows, totalSlots: getTotalSlotsForRows(normalizedNextRows) });
      setShowEditSavedModal(false);
      setShowAffectedModal(true);
      setNotifyPatientsMode(false);
      setError(null);
      setMessage("Affected bookings for the removed session are listed below.");
    } catch {
      notifyError("Unable to load affected bookings for this removed session.");
    }
  };

  const getEndedScheduleId = (rs) => {
    if (!Array.isArray(rs)) return null;
    for (const r of rs) if (r?.scheduleId && endedSessionStatus[r.scheduleId] === "ENDED") return r.scheduleId;
    return null;
  };

  /* ── save schedule ── */
  const prepareSaveSchedule = async () => {
    setError(null); setMessage(null); setShouldReopenAfterSave(false);
    const nr = normalizeRowsForBookingDate(rows, bookingDate);
    setRows(nr);
    if (!nr.length) { notifyError("Please add a new session before saving."); return; }
    const vErr = validateRows(nr, bookingDate);
    if (vErr) { notifyError(vErr); return; }
    const overlapErr = findOverlapWithExistingSchedule(nr, existingSchedule);
    if (overlapErr) { notifyError(overlapErr); return; }
    if (leaveState.onLeave) { notifyError("Cannot save schedule while the doctor is on leave. Cancel leave first."); return; }
    for (const row of nr) {
      if (row.scheduleId && endedSessionStatus[row.scheduleId] === "ENDED") {
        setReopenScheduleId(row.scheduleId); setShouldReopenAfterSave(true);
        setPendingAction("saveSchedule"); setPendingActionData({ rows: nr, totalSlots: getTotalSlotsForRows(nr) });
        setShowReopenModal(true); return;
      }
    }
    try {
      const affected = [];
      for (const s of existingSchedule) {
        if (!s.scheduleId) continue;
        const cid = auth?.clinic?.id || "";
        try {
          const r = await fetch(buildApiUrl(`appointments/doctor/bookings?scheduleId=${s.scheduleId}`), { headers: { "X-Clinic-Id": cid || "" } });
          if (!r.ok) continue;
          const bk = await r.json();
          if (!Array.isArray(bk)) continue;
          for (const b of bk) {
            const status = (b.status || b.bookingStatus || b.appointmentStatus || "").toString().toUpperCase();
            if (status === "COMPLETED") continue;
            const st = (b.slot_time || b.slot || b.slotTime || "").substring(0, 5);
            if (!st) continue;
            let inside = false;
            for (const nr2 of nr) {
              const ss = new Date(`1970-01-01T${nr2.startTime}:00`), se = new Date(`1970-01-01T${nr2.endTime}:00`), t = new Date(`1970-01-01T${st}:00`);
              if (t >= ss && t < se) { inside = true; break; }
            }
            if (!inside) affected.push(b);
          }
        } catch { }
      }
      if (affected.length > 0) {
        setAffectedAppointments(affected); setPendingAction("saveSchedule");
        setPendingActionData({ rows: nr, totalSlots: getTotalSlotsForRows(nr) });
        setShowAffectedModal(true); return;
      }
    } catch { }
    setPendingAction("saveSchedule"); setPendingActionData({ rows: nr, totalSlots: getTotalSlotsForRows(nr) });
    setShowActionConfirm(true);
  };

  const executeSaveSchedule = async (opts = {}) => {
    if (!pendingActionData?.rows) { notifyError("Unable to confirm schedule. Please try again."); setShowActionConfirm(false); return; }
    setSaving(true); setError(null); setMessage(null);
    try {
      if (opts.cancelBookings && Array.isArray(opts.cancelList)) {
        const cid = auth?.clinic?.id || "";
        for (const ap of opts.cancelList) {
          try { await fetch(buildApiUrl(`appointments/cancel/${ap.id}`), { method: "POST", headers: { "Content-Type": "application/json", "X-Clinic-Id": cid || "" } }); } catch { }
        }
      }
      const payloadRows = (existingSchedule.length > 0 && pendingActionData.rows.every((r) => !r.scheduleId))
        ? [...existingSchedule, ...pendingActionData.rows]
        : pendingActionData.rows;

      const resp = await fetch(buildApiUrl("slots/schedule"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          doctorId: Number(doctorId),
          bookingDate,
          sessions: toPayloadRows(payloadRows),
          deletedScheduleIds: pendingActionData.deletedScheduleIds || [],
          shouldReopen: shouldReopenAfterSave && reopenScheduleId ? reopenScheduleId : null,
        }),
      });
      const pl = await resp.json();
      if (pl.success) {
        notifySuccess(pl.message || "Schedule saved successfully.");
        setShowActionConfirm(false);
        if (shouldReopenAfterSave && reopenScheduleId) {
          try {
            const cid = auth?.clinic?.id || "";
            if (!cid) throw new Error("Clinic ID missing.");
            await fetch(buildApiUrl(`doctor-booking/opd/start/${reopenScheduleId}`), { method: "POST", headers: { "Content-Type": "application/json", "X-Clinic-Id": cid } });
          } catch { }
          setShouldReopenAfterSave(false); setReopenScheduleId(null);
        }
        const manageUrl = `/admin-dashboard/doctor-opd/${doctorId}/manage?date=${normalizeApiDate(bookingDate)}`;
        setTimeout(() => navigate(manageUrl), 650);
      } else { notifyError(pl.message || "Unable to save schedule."); setShowActionConfirm(false); }
    } catch { notifyError("Unable to save schedule. Check your network connection."); setShowActionConfirm(false); }
    finally { setSaving(false); }
  };

  const handleSaveLeave = async (payload) => {
    setError(null); setMessage(null); setSaving(true);
    try {
      const r = await fetch(buildApiUrl("slots/leave"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ doctorId: Number(doctorId), leaveDate: normalizeApiDate(bookingDate), leaveReason: payload.leaveReason || "" }) });
      const d = await r.json();
      if (d.success) { notifySuccess(d.message || "Leave marked."); setLeaveState({ onLeave: true, leaveReason: payload.leaveReason || "" }); setRows([makeEmptyRow(bookingDate)]); setShowLeaveModal(false); }
      else notifyError(d.message || "Unable to mark leave.");
    } catch { notifyError("Unable to save leave."); }
    finally { setSaving(false); }
  };

  const handleCancelLeave = async () => {
    setError(null); setMessage(null); setSaving(true);
    try {
      const r = await fetch(buildApiUrl(`slots/leave?doctorId=${doctorId}&date=${bookingDate}`), { method: "DELETE" });
      const d = await r.json();
      if (d.success) { notifySuccess(d.message || "Leave cancelled."); setLeaveState({ onLeave: false, leaveReason: "" }); setRows([makeEmptyRow(bookingDate)]); setShowLeaveModal(false); }
      else notifyError(d.message || "Unable to cancel leave.");
    } catch { notifyError("Unable to cancel leave."); }
    finally { setSaving(false); }
  };

  const editSavedSchedule = () => {
    if (!existingSchedule?.length) return;
    setEditModalRows(existingSchedule.map((s) => ({ startTime: s.startTime, endTime: s.endTime, slotDuration: s.slotDuration, scheduleId: s.scheduleId })));
    setSavedRowsBackup(rows.map((r) => ({ ...r }))); setShowEditSavedModal(true); setMessage(null); setError(null);
  };

  const checkAffectedBookings = async (newRows, options = {}) => {
    const removedScheduleIds = Array.isArray(options.removedScheduleIds)
      ? options.removedScheduleIds.filter(Boolean)
      : [];

    const targetSchedules = removedScheduleIds.length > 0
      ? existingSchedule.filter((s) => removedScheduleIds.includes(s.scheduleId))
      : existingSchedule;

    const affected = [];
    for (const s of targetSchedules) {
      if (!s.scheduleId) continue;
      try {
        const cid = auth?.clinic?.id || "";
        const r = await fetch(buildApiUrl(`appointments/doctor/bookings?scheduleId=${s.scheduleId}`), { headers: { "X-Clinic-Id": cid || "" } });
        if (!r.ok) continue;
        const bk = await r.json();
        if (!Array.isArray(bk)) continue;

        if (removedScheduleIds.length > 0) {
          for (const b of bk) {
            const status = (b.status || b.bookingStatus || b.appointmentStatus || "").toString().toUpperCase();
            if (status === "COMPLETED") continue;
            affected.push(b);
          }
          continue;
        }

        for (const b of bk) {
          const status = (b.status || b.bookingStatus || b.appointmentStatus || "").toString().toUpperCase();
          if (status === "COMPLETED") continue;
          const st = (b.slot_time || b.slot || b.slotTime || "").substring(0, 5);
          if (!st) continue;
          let inside = false;
          for (const nr of newRows) {
            const ss = new Date(`1970-01-01T${nr.startTime}:00`), se = new Date(`1970-01-01T${nr.endTime}:00`), t = new Date(`1970-01-01T${st}:00`);
            if (t >= ss && t < se) { inside = true; break; }
          }
          if (!inside) affected.push(b);
        }
      } catch { }
    }
    if (affected.length > 0) {
      setAffectedAppointments(affected); setPendingAction("saveSchedule");
      setPendingActionData({ rows: newRows, totalSlots: getTotalSlotsForRows(newRows), deletedScheduleIds: removedScheduleIds });
      setShowAffectedModal(true);
    } else {
      setAffectedAppointments([]); setPendingAction("saveSchedule");
      setPendingActionData({ rows: newRows, totalSlots: getTotalSlotsForRows(newRows), deletedScheduleIds: removedScheduleIds });
      setShowActionConfirm(true);
    }
  };

  const confirmEditSavedSchedule = async () => {
    const nr = normalizeRowsForBookingDate(editModalRows, bookingDate);
    const vErr = validateRows(nr, bookingDate);
    if (vErr) { notifyError(vErr); return; }

    const editedExistingIds = new Set(nr.map(r => r.scheduleId).filter(id => id && id.trim() !== ''));
    const newSessionsFromBackup = (savedRowsBackup || []).filter(r => !r.scheduleId || !editedExistingIds.has(r.scheduleId));
    const mergedRows = [...nr, ...newSessionsFromBackup];
    const removedScheduleIds = (existingSchedule || [])
      .filter((s) => s.scheduleId && !mergedRows.some((row) => row.scheduleId === s.scheduleId))
      .map((s) => s.scheduleId);

    setShowEditSavedModal(false); setError(null); setMessage(null); setRows(mergedRows); setShouldReopenAfterSave(false);
    const eid = getEndedScheduleId(mergedRows);
    if (eid) {
      setPendingAction("saveSchedule");
      setPendingActionData({ rows: mergedRows, totalSlots: getTotalSlotsForRows(mergedRows), deletedScheduleIds: removedScheduleIds });
      setReopenScheduleId(eid);
      setShouldReopenAfterSave(true);
      setShowReopenModal(true);
      return;
    }
    await checkAffectedBookings(mergedRows, { removedScheduleIds });
  };

  const undoLoad = () => { if (!savedRowsBackup) return; setRows(savedRowsBackup); setSavedRowsBackup(null); setUndoAvailable(false); notifySuccess("Restored previous editor rows."); };

  const handleConfirmReopen = async () => {
    if (!reopenScheduleId) return;
    setReopeningSession(true);
    try {
      if (shouldReopenAfterSave && pendingActionData?.rows) { setShowReopenModal(false); await checkAffectedBookings(pendingActionData.rows); setReopeningSession(false); return; }
      const cid = auth?.clinic?.id || "";
      if (!cid) { notifyError("Clinic ID missing. Please login again."); setShowReopenModal(false); return; }
      const r = await fetch(buildApiUrl(`doctor-booking/opd/start/${reopenScheduleId}`), { method: "POST", headers: { "Content-Type": "application/json", "X-Clinic-Id": cid } });
      const d = await r.json();
      if (d.success) {
        notifySuccess("Session reopened.");
        setShowReopenModal(false);
        setReopenScheduleId(null);
        setTimeout(() => navigate(`/admin-dashboard/doctor-opd/${doctorId}/manage?date=${normalizeApiDate(bookingDate)}`), 600);
      }
      else { notifyError(d.message || "Unable to reopen session."); setShowReopenModal(false); }
    } catch { notifyError("Error reopening session."); setShowReopenModal(false); }
    finally { setReopeningSession(false); }
  };

  const handleCancelReopen = () => {
    setShowReopenModal(false);
    setReopenScheduleId(null);
    setShouldReopenAfterSave(false);
    if (!shouldReopenAfterSave) {
      navigate(`/admin-dashboard/doctor-opd/${doctorId}/manage?date=${normalizeApiDate(bookingDate)}`);
    }
  };

  const handleDiscardDraft = () => {
    setRows([makeEmptyRow(bookingDate)]);
    setError(null); setMessage(null);
    pushToast("Draft discarded", "info");
  };

  const openLeaveModal = () => { setLeaveReasonDraft(leaveState.leaveReason || ""); setShowLeaveModal(true); };

  /* ── derived UI values ── */
  const dateOptions = [
    { label: "Today", value: getTodayISO() },
    { label: "Tomorrow", value: getTomorrowISO() },
    { label: "In 2 days", value: getPlusDaysISO(2) },
  ];
  const savedSlotTotal = existingSchedule.reduce((sum, s) => sum + (s.slotsCount || 0), 0);
  const newDraftSlots = rows.reduce((sum, r) => sum + calcSlots(r.startTime, r.endTime, r.slotDuration), 0);
  const step3Status = message ? "done" : saving ? "active" : "todo";
  const step2Status = step3Status === "done" ? "done" : loading ? "todo" : "active";
  const step1Status = loading ? "todo" : "done";
  const timelineRows = rows.filter((r) => r.startTime && r.endTime);
  const showTimeline = !loading && (existingSchedule.length > 0 || timelineRows.length > 0);

  /* ═══════════════════════════════ RENDER ══════════════════════════════ */
  return (
    <div className="opd-shell">
      <style>{STYLES}</style>
      <ToastStack toasts={toasts} />

      <div className="opd-page">
        {/* Topbar */}
        <div className="opd-topbar">
          <div className="opd-topbar-title">
            <div className="opd-avatar"><User size={20} /></div>
            <div>
              <h1>OPD Schedule — <span className="opd-topbar-doctor-name">{doctorName || "Doctor"}</span></h1>
              <p>Manage sessions, leave, and slot availability</p>
            </div>
          </div>
          <span className={`opd-doc-status ${leaveState.onLeave ? "off" : "ok"}`}>
            <i /> {leaveState.onLeave ? "On leave for this date" : "Accepting bookings"}
          </span>
        </div>

        {/* Hero */}
        <div className="opd-hero">
          <div className="opd-hero-eyebrow">OPD SCHEDULING</div>
          <h2>Manage OPD sessions and slot availability</h2>
          <p>View the saved schedule, add or edit sessions for this date, and publish. Changes go live for patients immediately.</p>
          <div className="opd-hero-grid">
            <div className="opd-hstat"><b>{existingSchedule.length}</b><span>Saved sessions</span></div>
            <div className="opd-hstat"><b>{savedSlotTotal}</b><span>Slots opened</span></div>
            <div className="opd-hstat"><b>{getTotalBookings(existingSessions)}</b><span>Total bookings</span></div>
            <div className="opd-hstat"><b>{rows.length}</b><span>New session{rows.length !== 1 ? "s" : ""} drafted</span></div>
          </div>
        </div>

        {/* Stepper */}
        <div className="opd-stepper">
          <div className={`opd-step2 ${step1Status}`}>
            <span className="opd-step2-dot">{step1Status === "done" ? <CheckCircle size={13} /> : "1"}</span>
            <span className="opd-step2-label">Review saved schedule</span>
          </div>
          <div className="opd-step2-rule" />
          <div className={`opd-step2 ${step2Status}`}>
            <span className="opd-step2-dot">{step2Status === "done" ? <CheckCircle size={13} /> : "2"}</span>
            <span className="opd-step2-label">Configure new sessions</span>
          </div>
          <div className="opd-step2-rule" />
          <div className={`opd-step2 ${step3Status}`}>
            <span className="opd-step2-dot">{step3Status === "done" ? <CheckCircle size={13} /> : "3"}</span>
            <span className="opd-step2-label">Save &amp; publish</span>
          </div>
        </div>

        <div className="opd-layout">
          {/* MAIN COLUMN */}
          <div className="opd-col-main">

            <div className="opd-card">
              <div className="opd-card-head">
                <div>
                  <h3><span className="opd-head-icon"><Calendar size={15} /></span>Booking date</h3>
                  <div className="opd-card-sub">Choose the day you're opening slots for</div>
                </div>
              </div>
              <div className="opd-date-row">
                {dateOptions.map((opt) => (
                  <button key={opt.label} type="button" className={`opd-chip2 ${bookingDate === opt.value ? "active" : ""}`} onClick={() => setBookingDate(opt.value)}>
                    {opt.label}
                  </button>
                ))}
                <div className="opd-date-picker2">
                  <Calendar size={15} />
                  <input type="date" value={bookingDate} min={getTodayISO()} onChange={(e) => setBookingDate(normalizeApiDate(e.target.value))} />
                </div>
              </div>
            </div>

            {leaveState.onLeave && (
              <div className="opd-alert2 opd-alert2-warn" style={{ marginTop: 0 }}>
                <AlertTriangle size={17} />
                <span><strong>Doctor is on leave — {bookingDate}.</strong> {leaveState.leaveReason || "No reason provided."}</span>
              </div>
            )}

            {loading ? (
              <div className="opd-skeleton2"><Loader2 size={18} className="opd-spin2" /> Loading schedule…</div>
            ) : (
              <>
                {showTimeline && (
                  <div className="opd-card">
                    <div className="opd-card-head">
                      <div>
                        <h3><span className="opd-head-icon"><Clock size={15} /></span>Day at a glance</h3>
                        <div className="opd-card-sub">Saved and drafted sessions across the day</div>
                      </div>
                    </div>
                    <div className="opd-timeline2">
                      {TL_HOURS.map((h) => (
                        <React.Fragment key={h}>
                          <div className="opd-tl2-tick" style={{ left: `${((h - TL_START) / (TL_END - TL_START)) * 100}%` }} />
                          <div className="opd-tl2-label" style={{ left: `${((h - TL_START) / (TL_END - TL_START)) * 100}%` }}>{hourLabel(h)}</div>
                        </React.Fragment>
                      ))}
                      {existingSchedule.map((s, idx) => (
                        <div key={`saved-${idx}`} className="opd-tl2-block saved" style={timelineStyle(s.startTime, s.endTime)} title={`Session ${idx + 1}: ${s.startTime}–${s.endTime}`}>
                          {s.startTime}–{s.endTime}
                        </div>
                      ))}
                      {timelineRows.map((r, idx) => (
                        <div key={`new-${idx}`} className="opd-tl2-block new" style={timelineStyle(r.startTime, r.endTime)} title={`New session: ${r.startTime}–${r.endTime}`}>
                          {r.startTime}–{r.endTime}
                        </div>
                      ))}
                    </div>
                    <div className="opd-tl2-legend">
                      <span><i style={{ background: "var(--opd-blue-700)" }} />Saved session</span>
                      <span><i style={{ background: "var(--opd-blue-500)" }} />New session</span>
                    </div>
                  </div>
                )}

                {existingSchedule.length > 0 && (
                  <div className="opd-card">
                    <div className="opd-card-head">
                      <div>
                        <h3><span className="opd-head-icon"><Layers size={15} /></span>Saved schedule <span className="opd-badge2">READ-ONLY</span></h3>
                        <div className="opd-card-sub">Session plan already published for this date</div>
                      </div>
                      <button type="button" className="opd-btn-ghost2" onClick={editSavedSchedule}><Edit3 size={13} /> Edit schedule</button>
                    </div>
                    <div>
                      {existingSchedule.map((s, idx) => (
                        <div className="opd-saved-row2" key={idx}>
                          <span className="opd-row-icon2"><Clock size={15} /></span>
                          <div>
                            <div className="opd-rtitle">Session {idx + 1}: {s.startTime} – {s.endTime}</div>
                            <div className="opd-rmeta">{s.slotDuration} min slots · {s.slotsCount} total slots</div>
                          </div>
                          <div className="opd-rcount2">{s.slotsCount}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 12, color: "var(--opd-ink-3)", fontSize: 12.5 }}>
                      {existingSchedule.length} session{existingSchedule.length > 1 ? "s" : ""} · {savedSlotTotal} total slots. Marking leave will cancel these.
                    </div>
                  </div>
                )}

                <div className="opd-card">
                  <div className="opd-card-head">
                    <div>
                      <h3><span className="opd-head-icon"><Plus size={15} /></span>New sessions</h3>
                      <div className="opd-card-sub">These will be added on top of the saved schedule</div>
                    </div>
                  </div>
                  <div className="opd-sessions2">
                    {rows.map((row, index) => {
                      const sc = calcSlots(row.startTime, row.endTime, row.slotDuration);
                      return (
                        <div key={index} className="opd-session2">
                          <div className="opd-session2-head">
                            <div className="opd-session2-title">
                              <span className="opd-session2-num">{existingSchedule.length + index + 1}</span>
                              Session {existingSchedule.length + index + 1}
                            </div>
                            <button type="button" className="opd-icon-btn2" onClick={() => removeRow(index)} disabled={leaveState.onLeave}>
                              <Trash2 size={13} /> Remove
                            </button>
                          </div>
                          <div className="opd-grid3">
                            <div className="opd-field2">
                              <label><Clock size={12} /> Start time</label>
                              <input type="time" className="opd-input2" value={row.startTime}
                                min={bookingDate === getTodayISO() ? getRoundedCurrentTimeISO() : undefined}
                                onChange={(e) => updateRow(index, "startTime", e.target.value)}
                                disabled={leaveState.onLeave} />
                            </div>
                            <div className="opd-field2">
                              <label><Clock size={12} /> End time</label>
                              <input type="time" className="opd-input2" value={row.endTime}
                                onChange={(e) => updateRow(index, "endTime", e.target.value)}
                                disabled={leaveState.onLeave} />
                            </div>
                            <div className="opd-field2">
                              <label><Layers size={12} /> Slot duration</label>
                              <select className="opd-input2" value={row.slotDuration}
                                onChange={(e) => updateRow(index, "slotDuration", Number(e.target.value))}
                                disabled={leaveState.onLeave}>
                                {durationOptions.map((o) => <option key={o} value={o}>{o} min</option>)}
                              </select>
                            </div>
                          </div>
                          {sc > 0 && (
                            <div className="opd-slot-pill2"><CheckCircle size={12} /> {sc} slot{sc !== 1 ? "s" : ""} will be created</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button type="button" className="opd-add-session2" onClick={addRow} disabled={leaveState.onLeave}>
                    <Plus size={14} /> Add another session
                  </button>

                  {error && (
                    <div className="opd-alert2 opd-alert2-error"><AlertTriangle size={17} /><span>{error}</span></div>
                  )}
                  {message && (
                    <div className="opd-alert2 opd-alert2-success"><CheckCircle size={17} /><span>{message}</span></div>
                  )}
                  {affectedAppointments?.length > 0 && (
                    <div className="opd-alert2 opd-alert2-warn"><Info size={17} /><span>{affectedAppointments.length} affected booking{affectedAppointments.length !== 1 ? "s" : ""} — resolve them from the dialog before saving.</span></div>
                  )}
                  {undoAvailable && (
                    <div className="opd-undo2">
                      <RotateCcw size={14} />
                      <span>Saved schedule loaded into editor.</span>
                      <button type="button" className="opd-undo2-btn" onClick={undoLoad}>Undo</button>
                    </div>
                  )}
                </div>

                {existingSessions.length > 0 && (
                  <div className="opd-card">
                    <div className="opd-card-head">
                      <div>
                        <h3><span className="opd-head-icon"><Users size={15} /></span>Existing sessions — {bookingDate}</h3>
                        <div className="opd-card-sub">Live status for sessions already opened on this date</div>
                      </div>
                    </div>
                    <div>
                      {existingSessions.map((s, idx) => (
                        <div className="opd-saved-row2" key={s.scheduleId || idx}>
                          <span className="opd-row-icon2"><Users size={15} /></span>
                          <div>
                            <div className="opd-rtitle">Session {s.sessionOrder || idx + 1}: {s.startTime} – {s.endTime}</div>
                            <div className="opd-rmeta">{s.slotDuration} min · {s.opdStatus || "UNKNOWN"}</div>
                          </div>
                          <div className="opd-rcount2" style={{ fontSize: 13 }}>{s.totalBookings || 0} booked</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 12, color: "var(--opd-ink-3)", fontSize: 12.5 }}>
                      {existingSessions.length} session{existingSessions.length > 1 ? "s" : ""} · {getTotalBookings(existingSessions)} total booking{getTotalBookings(existingSessions) !== 1 ? "s" : ""}. Marking leave will cancel these.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* SIDEBAR */}
          <div className="opd-col-side">
            <div className="opd-card">
              <h3 style={{ fontFamily: "var(--opd-font-disp)", fontSize: 15.5, fontWeight: 600, margin: "0 0 14px" }}>Doctor availability</h3>
              <div className={`opd-leave-card2 ${leaveState.onLeave ? "on" : ""}`}>
                <div className="opd-leave-status-row2">
                  <span className="opd-leave-icon2">{leaveState.onLeave ? <Ban size={16} /> : <CalendarCheck size={16} />}</span>
                  <div>
                    <div className="opd-leave-title2">{leaveState.onLeave ? `On leave — ${bookingDate}` : "Available for this date"}</div>
                    <div className="opd-leave-sub2">{leaveState.onLeave ? (leaveState.leaveReason || "No reason provided") : `No leave marked for ${bookingDate}`}</div>
                  </div>
                </div>
                <button type="button" className="opd-btn-block2" onClick={openLeaveModal} disabled={loadingLeave}>
                  {loadingLeave ? <Loader2 size={15} className="opd-spin2" /> : leaveState.onLeave ? <CalendarCheck size={15} /> : <CalendarOff size={15} />}
                  {loadingLeave ? "Checking…" : leaveState.onLeave ? "Manage leave" : "Mark leave"}
                </button>
              </div>
            </div>

            <div className="opd-card">
              <h3 style={{ fontFamily: "var(--opd-font-disp)", fontSize: 15.5, fontWeight: 600, margin: "0 0 14px" }}>Day summary</h3>
              <div className="opd-summary-list2">
                <div className="opd-summary-item2"><span>Saved sessions</span><b>{existingSchedule.length}</b></div>
                <div className="opd-summary-item2"><span>Saved slots</span><b>{savedSlotTotal}</b></div>
                <div className="opd-summary-item2"><span>Bookings so far</span><b>{getTotalBookings(existingSessions)}</b></div>
                <div className="opd-summary-item2"><span>New slots to open</span><b>{newDraftSlots}</b></div>
              </div>
            </div>

            <div className="opd-card">
              <h3 style={{ fontFamily: "var(--opd-font-disp)", fontSize: 15.5, fontWeight: 600, margin: "0 0 12px" }}>Before you publish</h3>
              <ul className="opd-tips-list2">
                <li>Sessions can't overlap — check the timeline above.</li>
                <li>Slot duration must divide the session length evenly.</li>
                <li>Marking leave cancels every session for this date.</li>
                <li>Bookings outside the new times will need rescheduling.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="opd-footer-space" />
      </div>

      {/* Sticky footer */}
      <div className="opd-footer2">
        <div className="opd-footer2-inner">
          <div className="opd-footer2-status">
            {message ? (<><CheckCircle size={14} color="var(--opd-green)" /> {message}</>)
              : leaveState.onLeave ? (<><Ban size={14} /> Doctor on leave — saving is disabled</>)
              : (<><span className="opd-dot-pulse2" /> Unsaved changes</>)}
          </div>
          <div className="opd-footer2-actions">
            <button type="button" className="opd-btn2 opd-btn2-secondary" onClick={handleDiscardDraft} disabled={saving || loading}>Discard</button>
            <button type="button" className="opd-btn2 opd-btn2-primary" onClick={prepareSaveSchedule} disabled={saving || loading || leaveState.onLeave || affectedAppointments?.length > 0}>
              {saving ? <><Loader2 size={17} className="opd-spin2" /> Saving…</> : <><Save size={17} /> Save schedule</>}
            </button>
          </div>
        </div>
      </div>

      {/* ─── Leave modal ─── */}
      {showLeaveModal && (
        <div className="opd-overlay2" onClick={(e) => { if (e.target === e.currentTarget && !saving) setShowLeaveModal(false); }}>
          <div className="opd-modal2">
            <div className="opd-modal2-head">
              <span className="opd-modal2-icon warn">{leaveState.onLeave ? <Ban size={19} /> : <CalendarOff size={19} />}</span>
              <div>
                <h2 className="opd-modal2-title">{leaveState.onLeave ? "Manage leave" : "Mark doctor leave"}</h2>
                <p className="opd-modal2-desc">{bookingDate}</p>
              </div>
            </div>
            <div className="opd-modal2-body">
              <div className="opd-field2">
                <label>LEAVE REASON · OPTIONAL</label>
                <textarea className="opd-input2" placeholder="e.g. Attending a medical conference" value={leaveReasonDraft} onChange={(e) => setLeaveReasonDraft(e.target.value)} disabled={leaveState.onLeave} />
              </div>
              <div className="opd-alert2 opd-alert2-warn"><AlertTriangle size={16} /><span>Marking leave will close booking slots and cancel any existing sessions for this date.</span></div>
            </div>
            <div className="opd-modal2-foot">
              <button type="button" className="opd-btn2 opd-btn2-secondary" onClick={() => setShowLeaveModal(false)} disabled={saving}>Close</button>
              {leaveState.onLeave ? (
                <button type="button" className="opd-btn2 opd-btn2-danger" onClick={handleCancelLeave} disabled={saving}>
                  {saving ? <><Loader2 size={15} className="opd-spin2" /> Cancelling…</> : <>Cancel leave</>}
                </button>
              ) : (
                <button type="button" className="opd-btn2 opd-btn2-primary" onClick={() => handleSaveLeave({ leaveReason: leaveReasonDraft })} disabled={saving}>
                  {saving ? <><Loader2 size={15} className="opd-spin2" /> Marking…</> : <>Mark leave</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Action confirm modal (save schedule) ─── */}
      <ConfirmModal
        open={showActionConfirm}
        title="Confirm opening slots"
        description={`You are about to open ${pendingActionData?.totalSlots || 0} slot${pendingActionData?.totalSlots !== 1 ? "s" : ""} across ${pendingActionData?.rows?.length || 0} session${pendingActionData?.rows?.length !== 1 ? "s" : ""} for ${bookingDate}.`}
        details={
          pendingActionData?.rows ? (
            <div className="opd-confirm-box2">
              {pendingActionData.rows.map((r, idx) => (
                <div className="opd-confirm-row2" key={idx}>
                  <span style={{ fontWeight: 600 }}>Session {idx + 1}: {r.startTime} – {r.endTime}</span>
                  <span style={{ color: "var(--opd-ink-3)" }}>{r.slotDuration} min · {calcSlots(r.startTime, r.endTime, r.slotDuration)} slots</span>
                </div>
              ))}
              <div className="opd-confirm-total2"><span>Total slots</span><span>{pendingActionData.totalSlots}</span></div>
            </div>
          ) : null
        }
        primaryLabel="Open slots"
        loading={saving}
        onConfirm={() => executeSaveSchedule()}
        onCancel={() => setShowActionConfirm(false)}
      />

      {/* ─── Edit saved schedule modal ─── */}
      {showEditSavedModal && (
        <div className="opd-overlay2">
          <div className="opd-modal2 opd-modal2-wide">
            <div className="opd-modal2-head">
              <span className="opd-modal2-icon"><Edit3 size={18} /></span>
              <div>
                <h2 className="opd-modal2-title">Edit saved schedule</h2>
                <p className="opd-modal2-desc">Update session times and slot durations. We'll check for affected bookings before saving.</p>
              </div>
            </div>
            <div className="opd-modal2-body">
              {error && <div className="opd-alert2 opd-alert2-error" style={{ marginTop: 0 }}><AlertTriangle size={16} />{error}</div>}
              <div className="opd-sessions2">
                {editModalRows.map((row, index) => (
                  <div className="opd-session2" key={index}>
                    <div className="opd-session2-head">
                      <div className="opd-session2-title"><span className="opd-session2-num">{index + 1}</span>Session {index + 1}</div>
                      <button type="button" className="opd-icon-btn2" onClick={() => removeEditRow(index)}><Trash2 size={13} /> Remove</button>
                    </div>
                    <div className="opd-grid3">
                      <div className="opd-field2"><label><Clock size={12} /> Start time</label><input type="time" className="opd-input2" value={row.startTime} onChange={(e) => updateEditRow(index, "startTime", e.target.value)} /></div>
                      <div className="opd-field2"><label><Clock size={12} /> End time</label><input type="time" className="opd-input2" value={row.endTime} onChange={(e) => updateEditRow(index, "endTime", e.target.value)} /></div>
                      <div className="opd-field2"><label><Layers size={12} /> Duration</label>
                        <select className="opd-input2" value={row.slotDuration} onChange={(e) => updateEditRow(index, "slotDuration", Number(e.target.value))}>
                          {durationOptions.map((o) => <option key={o} value={o}>{o} min</option>)}
                        </select>
                      </div>
                    </div>
                    {calcSlots(row.startTime, row.endTime, row.slotDuration) > 0 && (
                      <div className="opd-slot-pill2"><CheckCircle size={12} />{calcSlots(row.startTime, row.endTime, row.slotDuration)} slots</div>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" className="opd-btn-ghost2" style={{ marginTop: 14 }} onClick={() => setEditModalRows([...editModalRows, makeEmptyRow(bookingDate)])}>
                <Plus size={14} /> Add session
              </button>
            </div>
            <div className="opd-modal2-foot" style={{ justifyContent: "space-between" }}>
              <div style={{ color: "var(--opd-ink-3)", fontSize: 12, fontWeight: 600 }}>Review the sessions before saving</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button type="button" className="opd-btn2 opd-btn2-secondary" onClick={() => setShowEditSavedModal(false)}>Cancel</button>
                <button type="button" className="opd-btn2 opd-btn2-primary" onClick={confirmEditSavedSchedule}><Save size={14} /> Save &amp; confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Affected bookings modal ─── */}
      {showAffectedModal && (
        <div className="opd-overlay2">
          <div className="opd-modal2 opd-modal2-wide">
            <div className="opd-modal2-head">
              <span className="opd-modal2-icon warn"><AlertTriangle size={18} /></span>
              <div>
                <h2 className="opd-modal2-title">Affected bookings detected</h2>
                <p className="opd-modal2-desc">{affectedAppointments.length} booking{affectedAppointments.length === 1 ? "" : "s"} are impacted. Reschedule, cancel, or notify patients before saving.</p>
              </div>
            </div>
            <div className="opd-modal2-body">
              {notifyPatientsMode && (
                <div className="opd-alert2 opd-alert2-warn" style={{ marginTop: 0 }}><Info size={16} /><span>Confirm patients have been notified, then click "Confirm notified & save".</span></div>
              )}
              {availableSlotsForReschedule.length === 0 && (
                <div className="opd-alert2 opd-alert2-warn" style={{ marginTop: 0 }}><Info size={16} /><span>No available slots within new schedule to reschedule patients.</span></div>
              )}
              <div className="opd-affected-scroll2">
                {affectedAppointments.map((a) => (
                  <div key={a.id || `${a.slot_time}_${a.patient_name}`} className="opd-saved-row2" style={{ background: "var(--opd-surface-2)", borderColor: "var(--opd-line)" }}>
                    <span className="opd-row-icon2"><User size={15} /></span>
                    <div>
                      <div className="opd-rtitle">{a.patientName || a.patient_name || "Patient"} — {a.slot_time || a.slot || a.slotTime}</div>
                      <div className="opd-rmeta">{a.phone || a.patientPhone || "No phone"} · {a.status || "Booked"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                      {reschedulingId === a.id ? (
                        <>
                          <select className="opd-input2" style={{ width: "auto", minWidth: 130, fontSize: 13, height: 40, padding: "0 10px" }}
                            value={a._selectedSlot || ""}
                            onChange={(e) => setAffectedAppointments((prev) => prev.map((x) => x.id === a.id ? { ...x, _selectedSlot: e.target.value } : x))}>
                            <option value="">Select slot</option>
                            {availableSlotsForReschedule.map((s, i) => <option key={i} value={s}>{s}</option>)}
                          </select>
                          <button type="button" className="opd-btn2 opd-btn2-primary" style={{ height: 40, padding: "0 14px", fontSize: 13 }} onClick={async () => {
                            const chosen = a._selectedSlot;
                            if (!chosen) return notifyError("Select a slot to reschedule");
                            const base = pendingActionData?.rows || rows;
                            if (!isSlotInsideRows((chosen || "").substring(0, 5), base)) return notifyError("Selected slot is not within the new schedule times.");
                            try {
                              const form = new URLSearchParams();
                              form.append("appointmentId", a.id); form.append("date", normalizeApiDate(bookingDate)); form.append("slot", chosen);
                              const cid = auth?.clinic?.id || "";
                              const r = await fetch(buildApiUrl("appointments/reschedule"), { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded", "X-Clinic-Id": cid || "" }, body: form });
                              const d = await r.json();
                              if (!r.ok || !d.success) throw new Error(d.message || "Reschedule failed");
                              setAffectedAppointments((prev) => prev.filter((x) => x.id !== a.id));
                              setReschedulingId(null);
                              pushToast("Appointment rescheduled", "success");
                            } catch (err) { notifyError(err.message); }
                            finally { setReschedulingId(null); }
                          }}>Save</button>
                          <button type="button" className="opd-btn2 opd-btn2-secondary" style={{ height: 40, padding: "0 12px", fontSize: 13 }} onClick={() => setReschedulingId(null)}><X size={13} /></button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="opd-btn2 opd-btn2-outline" style={{ height: 38, padding: "0 14px", fontSize: 13 }} onClick={() => setReschedulingId(a.id)}>Reschedule</button>
                          <button type="button" className="opd-btn2 opd-btn2-secondary" style={{ height: 38, padding: "0 14px", fontSize: 13 }} onClick={async () => {
                            if (!window.confirm("Cancel this appointment?")) return;
                            try {
                              const cid = auth?.clinic?.id || "";
                              const r = await fetch(buildApiUrl(`appointments/cancel/${a.id}`), { method: "POST", headers: { "Content-Type": "application/json", "X-Clinic-Id": cid || "" } });
                              const d = await r.json();
                              if (!r.ok || !d.success) throw new Error(d.message || "Cancel failed");
                              setAffectedAppointments((prev) => prev.filter((x) => x.id !== a.id));
                              pushToast("Appointment cancelled", "success");
                            } catch (err) { notifyError(err.message); }
                          }}>Cancel</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="opd-modal2-foot">
              <button type="button" className="opd-btn2 opd-btn2-secondary" onClick={() => { setShowAffectedModal(false); setNotifyPatientsMode(false); setPendingAction(null); setPendingActionData(null); }}>Back</button>
              {notifyPatientsMode ? (
                <button type="button" className="opd-btn2 opd-btn2-primary" onClick={async () => { setShowAffectedModal(false); setNotifyPatientsMode(false); setMessage("Please contact patients to inform them of slot changes."); await executeSaveSchedule(); }}>
                  <CheckCircle size={14} /> Confirm notified &amp; save
                </button>
              ) : (
                <button type="button" className="opd-btn2 opd-btn2-outline" onClick={() => setNotifyPatientsMode(true)}>Notify patients</button>
              )}
              {!notifyPatientsMode && (
                <button type="button" className="opd-btn2 opd-btn2-danger" onClick={async () => { const list = affectedAppointments.slice(); setShowAffectedModal(false); setNotifyPatientsMode(false); await executeSaveSchedule({ cancelBookings: true, cancelList: list }); }}>
                  <Trash2 size={14} /> Cancel bookings &amp; save
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Reopen session modal ─── */}
      {showReopenModal && (
        <div className="opd-overlay2" onClick={handleCancelReopen}>
          <div className="opd-modal2" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="opd-modal2-head">
              <span className="opd-modal2-icon"><RefreshCw size={19} /></span>
              <div>
                <h2 className="opd-modal2-title">Session concluded</h2>
                <p className="opd-modal2-desc">
                  {shouldReopenAfterSave
                    ? "This OPD session has ended. The schedule times are being extended. Would you like to reopen the session so patients can be added to the queue?"
                    : "This OPD session was marked as concluded but the schedule has been updated. Would you like to reopen it now?"}
                </p>
              </div>
            </div>
            <div className="opd-modal2-foot">
              <button type="button" className="opd-btn2 opd-btn2-secondary" onClick={handleCancelReopen} disabled={reopeningSession}>Cancel</button>
              <button type="button" className="opd-btn2 opd-btn2-primary" onClick={handleConfirmReopen} disabled={reopeningSession}>
                {reopeningSession ? <><Loader2 size={15} className="opd-spin2" /> Reopening…</> : <><RefreshCw size={14} /> Reopen session</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}